import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyCareLogsApi, DailyCareCategory, DailyCareSubItem } from '../api/dailyCareLogs';

type NewSubDraft = {
  name: string;
  itemType: 'task' | 'observation' | 'yes_no' | 'dropdown' | 'checkbox' | 'short_text';
  allowedValues: string;
};

const DailyCareCategoriesAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<DailyCareCategory[]>([]);
  const [newSub, setNewSub] = useState<Record<number, NewSubDraft>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState<{ slug: string; name: string }>({ slug: '', name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dailyCareLogsApi.getCategories();
      setCategories(data);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getSubDraft = (categoryId: number): NewSubDraft =>
    newSub[categoryId] || { name: '', itemType: 'task', allowedValues: '' };

  const updateSubDraft = (categoryId: number, patch: Partial<NewSubDraft>) => {
    setNewSub(s => ({ ...s, [categoryId]: { ...getSubDraft(categoryId), ...patch } }));
  };

  const parseAllowed = (raw: string): string[] | null => {
    const values = raw.split(',').map(v => v.trim()).filter(Boolean);
    return values.length ? values : null;
  };

  const addSub = async (categoryId: number) => {
    const draft = getSubDraft(categoryId);
    const name = draft.name.trim();
    if (!name) return;
    setBusy(`add-${categoryId}`);
    try {
      await dailyCareLogsApi.createSubItem(categoryId, {
        name,
        itemType: draft.itemType,
        allowedValues: ['observation', 'dropdown', 'checkbox'].includes(draft.itemType) ? parseAllowed(draft.allowedValues) : null
      });
      setNewSub(s => ({ ...s, [categoryId]: { name: '', itemType: 'task', allowedValues: '' } }));
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to add sub-item');
    } finally {
      setBusy(null);
    }
  };

  const renameSub = async (id: number, name: string, original: string) => {
    if (name === original || !name.trim()) return;
    setBusy(`rename-${id}`);
    try {
      await dailyCareLogsApi.updateSubItem(id, { name: name.trim() });
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to rename sub-item');
    } finally {
      setBusy(null);
    }
  };

  const deleteSub = async (id: number, name: string) => {
    if (!window.confirm(`Deactivate sub-item "${name}"? It will disappear from the mobile form.`)) return;
    setBusy(`del-${id}`);
    try {
      await dailyCareLogsApi.deleteSubItem(id);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete sub-item');
    } finally {
      setBusy(null);
    }
  };

  const editAllowedValues = async (s: DailyCareSubItem) => {
    const current = (s.allowedValues || []).join(', ');
    const next = window.prompt(`Allowed values for "${s.name}" (comma-separated)`, current);
    if (next === null) return;
    const parsed = parseAllowed(next);
    setBusy(`vals-${s.id}`);
    try {
      await dailyCareLogsApi.updateSubItem(s.id, { allowedValues: parsed });
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update allowed values');
    } finally {
      setBusy(null);
    }
  };

  const createCategory = async () => {
    const slug = newCategory.slug.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    const name = newCategory.name.trim();
    if (!slug || !name) {
      setError('Slug and name are required');
      return;
    }
    setBusy('new-category');
    try {
      const maxOrder = Math.max(0, ...categories.map(c => c.displayOrder));
      await dailyCareLogsApi.createCategory(slug, name, maxOrder + 1);
      setNewCategory({ slug: '', name: '' });
      setShowNewCategory(false);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create category');
    } finally {
      setBusy(null);
    }
  };

  const deleteCategory = async (cat: DailyCareCategory) => {
    if (!window.confirm(`Deactivate category "${cat.name}"? Existing logs are preserved but the category will not appear in new forms.`)) return;
    setBusy(`del-cat-${cat.id}`);
    try {
      await dailyCareLogsApi.deleteCategory(cat.id);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete category');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Daily Care Categories</h1>
          <p className="text-sm text-gray-600 mt-1">
            Add, rename, or deactivate categories and sub-items. Observation items let carers pick a value (e.g. mood).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewCategory(s => !s)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
          >
            {showNewCategory ? 'Cancel' : '+ New Category'}
          </button>
          <button
            onClick={() => navigate('/daily-care-logs')}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            &larr; Back to logs
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}

      {showNewCategory && (
        <div className="bg-white rounded-lg shadow p-4 mb-6 border border-blue-100">
          <h3 className="font-semibold text-gray-900 mb-3">New Category</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              placeholder="Slug (e.g. mental_health)"
              value={newCategory.slug}
              onChange={e => setNewCategory(c => ({ ...c, slug: e.target.value }))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <input
              placeholder="Display name (e.g. Mental Health)"
              value={newCategory.name}
              onChange={e => setNewCategory(c => ({ ...c, name: e.target.value }))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              onClick={createCategory}
              disabled={busy === 'new-category'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              Create
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Medication-related slugs are blocked. Medication is handled separately via the MAR chart.
          </p>
        </div>
      )}

      {loading && <p className="text-gray-500 text-sm">Loading…</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(cat => {
          const draft = getSubDraft(cat.id);
          return (
            <div key={cat.id} className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 flex items-start justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">{cat.name}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">slug: {cat.slug}</p>
                </div>
                <button
                  onClick={() => deleteCategory(cat)}
                  disabled={busy === `del-cat-${cat.id}`}
                  className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50"
                >
                  Delete
                </button>
              </div>

              <ul className="divide-y divide-gray-200">
                {cat.subItems.length === 0 && (
                  <li className="px-4 py-3 text-sm text-gray-500 italic">No sub-items yet</li>
                )}
                {cat.subItems.map(s => (
                  <li key={s.id} className="px-4 py-2 flex items-center gap-2">
                    <input
                      defaultValue={s.name}
                      onBlur={e => renameSub(s.id, e.target.value, s.name)}
                      disabled={busy === `rename-${s.id}`}
                      className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                    />
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                      {s.itemType === 'yes_no' ? 'Yes/No' :
                       s.itemType === 'dropdown' ? 'Dropdown' :
                       s.itemType === 'checkbox' ? 'Checkbox' :
                       s.itemType === 'short_text' ? 'Short Text' :
                       s.itemType === 'observation' ? 'Observation' : 'Task'}
                    </span>
                    {['observation', 'dropdown', 'checkbox'].includes(s.itemType) && (
                      <button
                        onClick={() => editAllowedValues(s)}
                        disabled={busy === `vals-${s.id}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                        title="Edit allowed values"
                      >
                        Values ({(s.allowedValues || []).length})
                      </button>
                    )}
                    <button
                      onClick={() => deleteSub(s.id, s.name)}
                      disabled={busy === `del-${s.id}`}
                      className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>

              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
                <input
                  value={draft.name}
                  onChange={e => updateSubDraft(cat.id, { name: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') addSub(cat.id); }}
                  placeholder="New sub-item name"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <div className="flex gap-2 items-center">
                  <select
                    value={draft.itemType}
                    onChange={e => updateSubDraft(cat.id, { itemType: e.target.value as NewSubDraft['itemType'] })}
                    className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                  >
                    <option value="task">Task (Done/Partial/Refused/N/A)</option>
                    <option value="observation">Observation (pick from values)</option>
                    <option value="yes_no">Yes / No</option>
                    <option value="dropdown">Dropdown (single select)</option>
                    <option value="checkbox">Checkbox (multi select)</option>
                    <option value="short_text">Short Text (free input)</option>
                  </select>
                  {['observation', 'dropdown', 'checkbox'].includes(draft.itemType) && (
                    <input
                      value={draft.allowedValues}
                      onChange={e => updateSubDraft(cat.id, { allowedValues: e.target.value })}
                      placeholder="Allowed values, comma-separated (e.g. calm, confused, agitated)"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  )}
                  <button
                    onClick={() => addSub(cat.id)}
                    disabled={busy === `add-${cat.id}` || !draft.name.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyCareCategoriesAdmin;
